import { captureException } from '@sentry/react';
import { registerUser, useCountries, useIndustries } from 'app/api/RegistrationApi';
import { track } from "../core/App/track";
import { useStringsToSelectOptions } from "../core/stringsToSelectOptions";
import { useFormik } from 'formik';
import { InlineText, useToasts } from 'tombac';
import { registrationFormSchema } from "./registrationFormSchema";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const initialValues = {
  email: '',
  firstName: '',
  lastName: '',
  country: '',
  industry: '',
  company: {
    name: ''
  },
  products: [],
  termsAgreement: false
};

/**
 * Custom hook to manage the registration form.
 *
 * @param params - The parameters for the hook.
 * @returns An object containing formik instance, country options, industry
 *   options, and helper functions for form fields.
 */
export function useRegistrationForm(params) {
  const {
    onCreateUser
  } = params;
  const {
    addToast
  } = useToasts();
  const formik = useFormik({
    initialValues,
    validateOnMount: true,
    validationSchema: registrationFormSchema,
    async onSubmit(values) {
      values = registrationFormSchema.cast(values);
      try {
        await registerUser(values);
        onCreateUser && onCreateUser(values);
        trackFormSubmit(values);
      } catch (error) {
        console.error(error);
        captureException(typeof error === 'string' ? new Error(error) : error);
        addToast(typeof error === 'string' ? error : /*#__PURE__*/_jsxs(_Fragment, {
          children: ["An error occurred. Please try again or contact support at", ' ', /*#__PURE__*/_jsx(InlineText, {
            as: "a",
            fontSize: 12,
            fontWeight: "bold",
            href: "mailto:movesupport@tomtom.com",
            variant: "interactive",
            children: "movesupport@tomtom.com"
          }), "."]
        }), 'danger');
      }
    }
  });
  const [countries] = useCountries();
  const countryOptions = useStringsToSelectOptions(countries);
  const [industries] = useIndustries();
  const industryOptions = useStringsToSelectOptions(industries);
  const getFormGroupProps = name => ({
    caption: formik.touched[name] && formik.errors[name],
    danger: Boolean(formik.touched[name] && formik.errors[name])
  });
  const getInputProps = name => ({
    ...formik.getFieldProps(name),
    invalid: Boolean(formik.touched[name] && formik.errors[name])
  });
  const getProductCheckboxProps = productId => ({
    checked: formik.values.products.includes(productId),
    onBlur: () => formik.setFieldTouched('products', true),
    onChange: e => {
      formik.setFieldValue('products', e.currentTarget.checked ? [...formik.values.products, productId] : formik.values.products.filter(p => p !== productId));
    }
  });
  return {
    formik,
    countryOptions,
    industryOptions,
    getFormGroupProps,
    getInputProps,
    getProductCheckboxProps
  };
}

/**
 * Tracks the submission of the registration form.
 *
 * @param values - The values submitted in the registration form.
 */
function trackFormSubmit(values) {
  track('form_submit', {
    category: 'register',
    specificProps: {
      form_type: 'mp_registration',
      visitor_type: 'unknown',
      product_selection: values.products.join('|'),
      account_type: 'move',
      staticId: Buffer.from(values.email).toString('base64')
    }
  });
}